import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Alert, Spinner, Modal } from 'react-bootstrap';
import { useLazyGetEventsCalendarQuery } from '../app/api';
import { graphEventsRequest } from '../msalConfig';
import useGraphToken from './useGraphToken';
import { useIsMobile } from './utils';

// Upcoming Events (Hub → HR menu), as a month calendar.
//
// The firm's events calendar lives in a shared Outlook mailbox; HR sets which one
// in HR Portal → Config. This page reads it with the employee's own delegated
// token (Calendars.Read.Shared), read-only, so Exchange decides who can see it.
// Someone without read access on that calendar gets a plain "ask IT" message
// from the API rather than an empty month that looks like "no events".
//
// Dates are handled as 'YYYY-MM-DD' strings throughout, built from UTC Date
// maths. The server has already resolved every event to local calendar dates
// (first_day / last_day), so doing date arithmetic in the browser's zone here
// would only reintroduce the off-by-one it removed.

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const PATH = '/events';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS = 3;

const cardStyle = {
  background: 'white',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
  overflow: 'hidden',
};

const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

function btn(bg, fg) {
  return {
    background: bg, color: fg, border: 'none', borderRadius: 8,
    padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.85rem',
  };
}

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

function localToday() {
  const now = new Date();
  return iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

/** Every cell of a Sunday-first month grid, as whole weeks. */
function monthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  const start = addDays(first, -first.getUTCDay());
  const end = addDays(last, 6 - last.getUTCDay());
  const weeks = [];
  for (let d = start; d <= end; d = addDays(d, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => {
      const day = addDays(d, i);
      return { key: iso(day), date: day.getUTCDate(), inMonth: day.getUTCMonth() === month };
    }));
  }
  return { weeks, start: iso(start), end: iso(end) };
}

function monthTitle(year, month) {
  return new Date(Date.UTC(year, month, 1))
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function dayTitle(key) {
  return new Date(`${key}T00:00:00Z`)
    .toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

function Chip({ ev, day }) {
  // A multi-day event shows on every day it covers, filled like an all-day
  // event; the arrow on the days after its first marks it as a continuation.
  const spans = ev.first_day !== ev.last_day;
  const continues = spans && day !== ev.first_day;
  const filled = ev.is_all_day || spans;
  return (
    <div
      title={`${ev.subject} (${ev.time_label})`}
      style={{
        fontSize: '0.72rem', lineHeight: 1.3, padding: '1px 5px', marginTop: 2,
        borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        background: filled ? brand.tealBlue : 'transparent',
        color: filled ? 'white' : brand.deepBlue,
        fontWeight: filled ? 600 : 500,
      }}
    >
      {continues && <i className="bi bi-arrow-right-short" aria-hidden="true" />}
      {!filled && <strong style={{ color: brand.charcoal, marginRight: 3 }}>{ev.start_label.replace(':00', '')}</strong>}
      {ev.subject}
    </div>
  );
}
Chip.propTypes = {
  ev: PropTypes.object.isRequired,
  day: PropTypes.string.isRequired,
};

function DayCell({ cell, events, isToday, selected, compact, onSelect }) {
  const shown = events.slice(0, compact ? 0 : MAX_CHIPS);
  const more = events.length - shown.length;
  return (
    <button
      type="button"
      onClick={() => onSelect(cell.key)}
      aria-pressed={selected}
      aria-label={`${dayTitle(cell.key)}, ${events.length ? `${events.length} event${events.length === 1 ? '' : 's'}` : 'no events'}`}
      style={{
        display: 'block', width: '100%', minWidth: 0, textAlign: 'left', verticalAlign: 'top',
        minHeight: compact ? 54 : 104, padding: compact ? '4px 2px' : '4px 5px',
        border: 'none', borderTop: `1px solid ${brand.softGray}`, borderLeft: `1px solid ${brand.softGray}`,
        background: selected ? 'rgba(208,155,44,0.12)' : (cell.inMonth ? 'white' : '#faf9f7'),
        boxShadow: selected ? `inset 0 0 0 2px ${brand.goldenrod}` : 'none',
        cursor: 'pointer',
      }}
    >
      <div style={{ textAlign: compact ? 'center' : 'left' }}>
        <span style={{
          display: 'inline-block', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center',
          borderRadius: 11, fontSize: '0.78rem', fontWeight: isToday ? 800 : 600,
          background: isToday ? brand.goldenrod : 'transparent',
          color: isToday ? brand.deepBlue : (cell.inMonth ? brand.deepBlue : '#a8a6a2'),
        }}>
          {cell.date}
        </span>
      </div>
      {shown.map((ev, i) => <Chip key={`${ev.subject}-${ev.first_day}-${i}`} ev={ev} day={cell.key} />)}
      {more > 0 && (
        compact ? (
          // On a phone the cell is ~50px wide: a count, not titles. The day's
          // events are listed in full under the grid when it's tapped.
          <div style={{ textAlign: 'center', marginTop: 2 }}>
            <span style={{
              display: 'inline-block', minWidth: 18, borderRadius: 9, padding: '0 5px',
              fontSize: '0.68rem', fontWeight: 700, background: brand.tealBlue, color: 'white',
            }}>
              {more}
            </span>
          </div>
        ) : (
          <div style={{ fontSize: '0.7rem', color: brand.charcoal, fontWeight: 700, marginTop: 2, paddingLeft: 5 }}>
            +{more} more
          </div>
        )
      )}
    </button>
  );
}
DayCell.propTypes = {
  cell: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired,
  isToday: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  compact: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function EventDetail({ ev, first, stacked }) {
  // Stacked on a phone: a fixed time column beside the title leaves the title
  // ~150px in a modal that wide.
  return (
    <div className={stacked ? '' : 'd-flex gap-3'} style={{ padding: '0.75rem 0', borderTop: first ? 'none' : `1px solid ${brand.softGray}` }}>
      <div style={{ flex: '0 0 auto', width: stacked ? 'auto' : 120, fontSize: '0.8rem', fontWeight: 700, color: ev.is_all_day ? brand.goldenrod : brand.tealBlue }}>
        {ev.time_label}
      </div>
      <div style={{ ...WRAP, flex: 1 }}>
        <div style={{ color: brand.deepBlue, fontWeight: 700 }}>{ev.subject}</div>
        {(ev.location || ev.is_online_meeting) && (
          <div style={{ fontSize: '0.8rem', color: brand.charcoal }}>
            <i className={`bi bi-${ev.location ? 'geo-alt' : 'camera-video'} me-1`} />
            {ev.location || 'Online'}
          </div>
        )}
        {ev.preview && (
          <div style={{ fontSize: '0.82rem', color: brand.charcoal, marginTop: 4, whiteSpace: 'pre-line' }}>
            {ev.preview}
          </div>
        )}
      </div>
    </div>
  );
}
EventDetail.propTypes = {
  ev: PropTypes.object.isRequired,
  first: PropTypes.bool,
  stacked: PropTypes.bool,
};

export default function Events() {
  const isMobile = useIsMobile();
  const [load, res] = useLazyGetEventsCalendarQuery();

  const today0 = localToday();
  const [ym, setYm] = useState(() => ({ y: Number(today0.slice(0, 4)), m: Number(today0.slice(5, 7)) - 1 }));
  const [selected, setSelected] = useState(today0);
  // The day whose details are open in the modal, or null.
  const [openDay, setOpenDay] = useState(null);
  const grid = useMemo(() => monthGrid(ym.y, ym.m), [ym]);

  // The token callback reads the range from a ref: it is also how a month change
  // fetches (acquire → silent token → onToken), so it must see the NEW month.
  const rangeRef = useRef(grid);
  rangeRef.current = grid;

  const onToken = useCallback((accessToken, opts) => {
    const { start, end } = rangeRef.current;
    load({ graphToken: accessToken, start, end }, opts?.preferCache !== false);
  }, [load]);

  const { phase, authError, connecting, needsReload, connect, acquire, hubEmail } =
    useGraphToken(graphEventsRequest, PATH, onToken);

  const goMonth = useCallback((delta) => {
    const d = new Date(Date.UTC(ym.y, ym.m + delta, 1));
    const next = { y: d.getUTCFullYear(), m: d.getUTCMonth() };
    rangeRef.current = monthGrid(next.y, next.m);
    setYm(next);
    setSelected(iso(d));
    if (phase === 'ready') acquire({ preferCache: true });
  }, [ym, phase, acquire]);

  const goToday = useCallback(() => {
    const t = localToday();
    const next = { y: Number(t.slice(0, 4)), m: Number(t.slice(5, 7)) - 1 };
    rangeRef.current = monthGrid(next.y, next.m);
    setYm(next);
    setSelected(t);
    if (phase === 'ready') acquire({ preferCache: true });
  }, [phase, acquire]);

  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await acquire({ preferCache: false });
    } finally {
      setRefreshing(false);
    }
  }, [acquire]);

  const data = res.data;
  // Only trust events that belong to the grid on screen: while a new month is
  // loading, the previous month's response is still in `data`.
  const current = data && data.start === grid.start && data.end === grid.end ? data : null;
  const today = current?.today || today0;

  const byDay = useMemo(() => {
    const m = {};
    for (const ev of current?.events || []) {
      for (let d = new Date(`${ev.first_day}T00:00:00Z`); iso(d) <= ev.last_day; d = addDays(d, 1)) {
        const k = iso(d);
        if (k < grid.start || k > grid.end) continue;
        (m[k] = m[k] || []).push(ev);
      }
    }
    return m;
  }, [current, grid]);

  // Clicking a day highlights it; the details pop up only when there is
  // something to show, so a stray click on an empty day costs nothing.
  const onSelectDay = useCallback((key) => {
    setSelected(key);
    if ((byDay[key] || []).length) setOpenDay(key);
  }, [byDay]);
  const openEvents = (openDay && byDay[openDay]) || [];

  let body;
  if (phase === 'connect') {
    body = (
      <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center' }}>
        <i className="bi bi-calendar-event-fill" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>Connect Outlook</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 480, margin: '0 auto 1rem' }}>
          Events come from the PCA Company Calendar in Outlook. This is read-only and nothing is
          changed in your calendar. Connecting {hubEmail}.
        </p>
        {needsReload ? (
          <button type="button" onClick={() => window.location.reload()} style={btn(brand.deepBlue, 'white')}>
            <i className="bi bi-arrow-clockwise me-1" />Reload and try again
          </button>
        ) : (
          <button type="button" onClick={() => connect(isMobile)} disabled={connecting}
            style={btn(connecting ? brand.softGray : brand.goldenrod, brand.deepBlue)}>
            {connecting ? 'Waiting for Microsoft…' : 'Connect Outlook'}
          </button>
        )}
        {authError && <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>{authError}</Alert>}
      </div>
    );
  } else if (res.isError) {
    body = (
      <Alert variant="warning" style={{ fontSize: '0.9rem' }}>
        {res.error?.data?.detail || 'The events calendar could not be read just now. Please try again shortly.'}
      </Alert>
    );
  } else {
    const loading = !current || res.isFetching;
    body = (
      <>
        <div style={{ ...cardStyle, position: 'relative' }}>
          <div className="d-flex align-items-center gap-2" style={{ padding: '0.6rem 0.75rem', background: brand.deepBlue }}>
            <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month"
              style={{ ...btn('transparent', 'white'), padding: '0.25rem 0.5rem' }}>
              <i className="bi bi-chevron-left" />
            </button>
            <h5 className="mb-0" style={{ color: 'white', fontWeight: 800, flex: 1, textAlign: 'center', fontSize: isMobile ? '1rem' : '1.15rem' }}>
              {monthTitle(ym.y, ym.m)}
            </h5>
            <button type="button" onClick={() => goMonth(1)} aria-label="Next month"
              style={{ ...btn('transparent', 'white'), padding: '0.25rem 0.5rem' }}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderRight: `1px solid ${brand.softGray}` }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{
                textAlign: 'center', padding: '0.35rem 0', fontSize: '0.72rem', fontWeight: 800,
                letterSpacing: '0.04em', textTransform: 'uppercase', color: brand.charcoal,
                borderLeft: `1px solid ${brand.softGray}`, background: '#f6f5f2',
              }}>
                {isMobile ? w.charAt(0) : w}
              </div>
            ))}
            {grid.weeks.flat().map((cell) => (
              <DayCell
                key={cell.key}
                cell={cell}
                events={byDay[cell.key] || []}
                isToday={cell.key === today}
                selected={cell.key === selected}
                compact={isMobile}
                onSelect={onSelectDay}
              />
            ))}
          </div>

          {loading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.6)', color: brand.charcoal, fontSize: '0.9rem',
            }}>
              <Spinner animation="border" size="sm" className="me-2" />Loading events…
            </div>
          )}
        </div>

        <Modal show={openEvents.length > 0} onHide={() => setOpenDay(null)} centered scrollable>
          <Modal.Header closeButton style={{ borderBottom: `3px solid ${brand.goldenrod}` }}>
            <Modal.Title style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.1rem' }}>
              {openDay && dayTitle(openDay)}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
            {openEvents.map((ev, i) => (
              <EventDetail key={`${ev.subject}-${ev.first_day}-${i}`} ev={ev} first={i === 0} stacked={isMobile} />
            ))}
          </Modal.Body>
        </Modal>
      </>
    );
  }

  return (
    <Container fluid className="py-3 px-md-4" style={{ maxWidth: 1200 }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h4 className="mb-0" style={{ color: brand.deepBlue, fontWeight: 800 }}>Upcoming Events</h4>
          <div style={{ color: brand.charcoal, fontSize: '0.84rem' }}>
            What&rsquo;s happening at Prosperity. Click a day to see its details.
          </div>
        </div>
        {phase !== 'connect' && (
          <>
            <button type="button" onClick={goToday} style={btn(brand.softGray, brand.deepBlue)}>
              Today
            </button>
            <button type="button" onClick={refresh} disabled={refreshing || res.isFetching}
              style={btn(brand.softGray, brand.deepBlue)}>
              <i className="bi bi-arrow-clockwise me-1" />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </>
        )}
      </div>
      {body}
    </Container>
  );
}
